// Locale-aware accessor for a city landing page's hand-written intro copy.
// Unlike event/tour translations (produced by an OpenAI rewrite pipeline and
// read off the DB row), this content is authored directly per locale and
// lives in the i18n resource files — there are only 7 cities and it rarely
// changes, so a DB column + Edge Function would be pure overhead. Reads the
// imported resource JSON directly (same import src/lib/i18n/index.ts uses)
// rather than going through react-i18next's t(), which avoids the
// `returnObjects` handling a structured `intro` array would otherwise need.

import guideEn from '@/lib/i18n/city-guides/en.json';
import guideFr from '@/lib/i18n/city-guides/fr.json';
import guideRu from '@/lib/i18n/city-guides/ru.json';
import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

export type CityPageCopy = {
  title: string;
  metaDescription: string;
  intro: string[];
};

type CityPagesResource = { cityPages: Record<string, CityPageCopy> };

const resources: Record<Locale, CityPagesResource> = { en, fr, ru };

/** A city's hand-written landing-page copy, or undefined if not yet authored. */
export function cityPageCopy(cityId: string, locale: Locale): CityPageCopy | undefined {
  return resources[locale].cityPages[cityId];
}

// The long-form "city guide" below the listings — things to do, events and
// when to visit, getting there — plus a per-city FAQ (also emitted as FAQPage
// JSON-LD). Hand-written per locale like the intro, but kept in its own
// resource files (src/lib/i18n/city-guides/) rather than the UI string
// bundles: it's page content, not interface copy, and far longer.

export type CityGuideSection = { heading: string; paragraphs: string[] };
export type CityGuideFaqEntry = { q: string; a: string };
export type CityGuide = { sections: CityGuideSection[]; faq: CityGuideFaqEntry[] };

type CityGuidesResource = { faqHeading: string; cities: Record<string, CityGuide> };

const guides: Record<Locale, CityGuidesResource> = { en: guideEn, fr: guideFr, ru: guideRu };

/** A city's hand-written guide, or undefined if not yet authored. */
export function cityGuide(cityId: string, locale: Locale): CityGuide | undefined {
  return guides[locale].cities[cityId];
}

/** The localized "Frequently asked questions" heading for the city guide FAQ. */
export function cityGuideFaqHeading(locale: Locale): string {
  return guides[locale].faqHeading;
}
